/**
 * Referral Manager - Handles wallet-based referral tracking and rewards calculation
 */

import { AFFILIATE_COMMISSION_RATE, AFFILIATE_POOL_ALLOCATION } from './constants';
import { PublicKey } from '@solana/web3.js';

/**
 * Generate a referral link for a user
 * @param {string} walletAddress - The wallet address of the referrer
 * @returns {string} - The referral link
 */
export const generateReferralLink = (walletAddress) => {
  if (!walletAddress) return '';
  
  // Create base URL using current location
  const baseUrl = window.location.origin + window.location.pathname;
  
  // Encode the wallet address as the ref parameter
  return `${baseUrl}?ref=${encodeURIComponent(walletAddress)}`;
};

/**
 * Extract referrer wallet address from URL
 * @returns {string|null} - The referrer's wallet address or null
 */
export const extractReferrerFromUrl = () => {
  // In a browser environment, use window.location.search
  const urlParams = new URLSearchParams(window.location.search);
  const ref = urlParams.get('ref');
  return ref ? decodeURIComponent(ref) : null;
};

/**
 * Store referrer information in local storage
 * @param {string} referrerWallet - The wallet address of the referrer
 */
export const storeReferrer = (referrerWallet) => {
  if (!referrerWallet) return;
  
  try {
    // Validate that it's a proper Solana address
    new PublicKey(referrerWallet);
    localStorage.setItem('palmheist_referrer', referrerWallet);
  } catch (error) {
    console.error('Invalid referrer wallet address:', error);
  }
};

/**
 * Get stored referrer from local storage
 * @returns {string|null} - The stored referrer wallet address or null
 */
export const getStoredReferrer = () => {
  return localStorage.getItem('palmheist_referrer');
};

/**
 * Calculate affiliate rewards based on user's JINU allocation
 * @param {number} jinuAmount - The amount of JINU tokens allocated to the user
 * @returns {number} - The affiliate reward amount (10% of user's JINU)
 */
export const calculateAffiliateReward = (jinuAmount) => {
  return jinuAmount * AFFILIATE_COMMISSION_RATE;
};

/**
 * Format referral stats for display
 * @param {Object} stats - The referral statistics object
 * @returns {Object} - Formatted stats for display
 */
export const formatReferralStats = (stats = {}) => {
  return {
    totalReferrals: stats.count || 0,
    referralSol: stats.volume?.toFixed(2) || '0',
    referralEarnings: stats.earnings?.toFixed(2) || '0'
  };
};
